import axios from "axios";
import { router } from "../router";
import AuthUtils from "../utils/auth.utils";

/*
 * Global Axios response interceptor
 * --------------------------------
 * 1. Listens for any 401 (Unauthorized) or 419 (Token/Session expired) responses.
 * 2. Clears stored authentication data so the application state is reset.
 * 3. Redirects the user to the login page, preserving the current route for a seamless return after successful authentication.
 *
 * Having a single, centralised handler ensures that as soon as the backend invalidates
 * an access token, the frontend reacts immediately – instead of letting a few API
 * calls fail silently before other parts of the app notice.
 */
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error && error.response) {
      const { status, data } = error.response;
      const errorMessage =
        (typeof data === "string" && data) ||
        (data?.message ? data.message.toString() : "");

      const tokenProbablyInvalid =
        status === 401 ||
        status === 419 ||
        status === 403 ||
        // Laravel decrypt exception when APP_KEY / Passport keys change
        (status >= 500 &&
          /unauth|token|invalid|decrypt|mac/i.test(errorMessage));

      if (tokenProbablyInvalid) {
        // Make sure we only perform the logout routine once per invalidation event
        // to avoid potential navigation loops.
        if (AuthUtils.isAuthenticated()) {
          AuthUtils.clearAuthData();
        }

        const currentRoute = router.currentRoute;
        const isOnLoginRoute = currentRoute?.name === "AuthLogin";

        if (!isOnLoginRoute) {
          // Preserve the attempted route so the user can return after logging back in
          const redirectPath = currentRoute?.fullPath || "/";
          AuthUtils.redirectToLogin(redirectPath);
        }
      }
    }

    return Promise.reject(error);
  }
);

export default axios;
